/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  provideAppInitializer,
  inject,
  isDevMode,
  ErrorHandler,
  APP_INITIALIZER,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';

import { routes } from './app.routes';
import { provideServiceWorker } from '@angular/service-worker';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { firstValueFrom, tap, map, switchMap, from, catchError, of } from 'rxjs';
import { Config } from './shared/models/config.model';
import {
  CsrfService,
  ConfigService,
  authInterceptor,
  xsrfInterceptor,
  AuthService,
} from '@ash-workspace/core';


import * as Sentry from "@sentry/angular";

import { LocalStorageService } from './shared/services/storage/local-storage.service';

async function cleanupServiceWorkerInDevMode(): Promise<void> {
  if (!isDevMode() || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (error) {
    console.warn('Service-Worker-Cleanup im Dev-Modus fehlgeschlagen:', error);
  }
}

function initializeApp() {
  const configService = inject(ConfigService);
  const localStorage = inject(LocalStorageService);
  const csrfService = inject(CsrfService);
  const authService = inject(AuthService);
  let configStored = true;
  const config: Config = {
    apiUrl: '',
    gatewayUrl: '',
    vapidPublicKey: '',
  };
  Object.keys(config).forEach((key) => {
    if (!localStorage.getItem(key)) configStored = false;
    else {
      config[key as keyof Config] = localStorage.getItem(key)!;
    }
  });
  const loggedIn =
    !!localStorage.getItem('isLoggedIn') || !!localStorage.getItem('loggedIn');
  if(configStored){
    configService.setConfig(config as Config);
  }
  if (configStored && loggedIn) {
    authService.setApiUrl(config.gatewayUrl);
    return cleanupServiceWorkerInDevMode().then(async () => {
      try {
        await csrfService.initCsrf(config.gatewayUrl);
      } catch (error) {
        console.warn('CSRF-Token konnte nicht geladen werden:', error);
      }

      return authService.initLoggedIn();
    });
  }

  const httpClient = inject(HttpClient);

  return cleanupServiceWorkerInDevMode().then(() =>
    firstValueFrom(
      httpClient.get<Config>('/config.json').pipe(
        tap((config) => {
          configService.setConfig(config);
          authService.setApiUrl(configService.getConfig('gatewayUrl')!);
        }),
        catchError((err) => {
          console.error('Config Ladefehler:', err);
          throw new Error('Config nicht geladen');
        }),
        switchMap((config) => {
          return from(csrfService.initCsrf(configService.getConfig('gatewayUrl')!)).pipe(
            map(() => config),
            catchError((err) => {
              authService.setIsOffline(true);
              console.error('CSRF Service Fehler:', err);
              return of(config);
            }),
          );
        }),
        switchMap((config) => {
          return from(authService.initLoggedIn()).pipe(
            map(() => config),
            catchError((err) => {
              authService.setIsOffline(true);
              console.error('Auth Service Fehler:', err);
              return of(config);
            }),
          );
        }),
      ),
    ),
  );
}

export const appConfig: ApplicationConfig = {
  providers: [

    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptors([authInterceptor, xsrfInterceptor])),
    provideAppInitializer(initializeApp),
    provideRouter(routes),
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:2000',
    }),
         {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
};
